import { computed, type MaybeRef, type Ref, unref } from 'vue';
import type { Pfm } from '@aviata/platform-client';
import { toSafeInteger, isNil } from 'lodash-es';
import { useFreedomCashbackQuery } from '../api';
import type { ProductName } from '../types';

interface FreedomCashbackModel {
  amount: Ref<number>;
  isLoading: Ref<boolean>;
  isError: Ref<boolean>;
  isBankClient: Ref<boolean>;
  cashbackPercent: Ref<number>;
  levelName: Ref<string>;
}

interface FreedomCashbackProps {
  pfm: Pfm;
  billId?: MaybeRef<string | undefined>;
  offerPrice: MaybeRef<string | number | undefined>;
  productName?: ProductName;
  enabled?: MaybeRef<boolean>;
}

export function prefetchFreedomCashback({
  pfm,
  billId,
  productName,
  enabled,
}: {
  pfm: Pfm;
  billId?: MaybeRef<string | undefined>;
  productName?: ProductName;
  enabled?: MaybeRef<boolean>;
}) {
  useFreedomCashbackQuery({ pfm, billId, productName, enabled });
}

export function useFreedomCashback({
  pfm,
  billId,
  offerPrice,
  productName,
  enabled,
}: FreedomCashbackProps): FreedomCashbackModel {
  const freedomCashbackQuery = useFreedomCashbackQuery({
    pfm,
    billId,
    productName,
    enabled,
  });

  const isLoading = computed(() => freedomCashbackQuery.isFetching.value);
  const isError = computed(() => freedomCashbackQuery.isError.value);

  const cashbackPercent = computed(() =>
    toSafeInteger(freedomCashbackQuery.data.value?.total_cashback_percent),
  );

  const amount = computed(() => {
    const price = unref(offerPrice);

    if (isNil(price)) {
      return 0;
    }

    const data = unref(freedomCashbackQuery.data);

    const transactionCashback =
      (toSafeInteger(price) * toSafeInteger(cashbackPercent.value)) / 100;

    const effectiveCashback = Math.min(
      transactionCashback,
      toSafeInteger(data?.limit_per_transaction),
    );

    const remainingMonthlyLimit =
      toSafeInteger(data?.limit_per_month) -
      toSafeInteger(data?.received_cashback_sum);

    return Math.floor(Math.min(effectiveCashback, remainingMonthlyLimit));
  });

  const isBankClient = computed(() =>
    Boolean(freedomCashbackQuery.data.value?.found),
  );

  const levelName = computed(() => freedomCashbackQuery.data.value?.level_name);

  return {
    amount,
    isLoading,
    isError,
    isBankClient,
    cashbackPercent,
    levelName,
  };
}
